import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Divider,
  TextField,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import { InfoOutlined } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DashboardHeader from "@components/Header/DashboardHeader";
import { AppDispatch, RootReducerType } from "@store/store";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { postFinalBgReUploadThunk } from "@store/thunk/PaymentThunk";
import {
  getUploadSignedURLThunk,
  getTransactionStatusThunk,
  fetchApplicationDetailsRow,
  fetchApplicationDetails,
} from "@modules/provisional-payment/redux/thunk/application-detailThunk";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  FeesDetailsThunk,
  FeesDetailsComponentThunk,
} from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import BgConfirmationModal from "./BgConfirmationModal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { getFileSignedURLThunk as getAccessSignedURLThunk } from "@modules/stepper/redux/thunk/documentThunk";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { getFileSignedURLThunk as getRowSignedURLThunk } from "@modules/stepper-row/redux/thunk/documentThunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";

export const BgReupload: React.FC = () => {
  const [bankGuaranteeNumber, setBankGuaranteeNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankGuaranteeDoc, setBankGuaranteeDoc] = useState<File | null>(null);
  const [branchAddress, setBranchAddress] = useState("");
  const [bgIssuanceDate, setBgIssuanceDate] = useState("");
  const [bgValidityDate, setBgValidityDate] = useState("");
  const [bgClaimDate, setBgClaimDate] = useState("");
  const [bankGauranteeURL, setBankGauranteeURL] = useState<string | null>(null);
  const [applicantData, setApplicantData] = useState<any>(null);
  const [provisionalFee, setProvisionalFee] = useState<any>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openClaimDate, setOpenClaimDate] = React.useState(false);
  const [openIssuanceDate, setOpenIssuanceDate] = React.useState(false);
  const [openValidityDate, setOpenValidityDate] = React.useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const params = new URLSearchParams(location.search);
  const row = params.get("row");
  const access = params.get("access");
  const applicationId = row || access;

  // Input validation functions
  const validateBankName = (value: string) => {
    return value.replace(/[^a-zA-Z\s]/g, "");
  };

  const validateBankGuaranteeNumber = (value: string) => {
    return value.replace(/[^a-zA-Z0-9]/g, "");
  };

  const validateBranchAddress = (value: string) => {
    return value.replace(/[^a-zA-Z0-9\s,.\/#-]/g, "");
  };

  // Fetch application data
  const fetchApplicationData = async () => {
    if (!applicationId) return;

    try {
      if (row) {
        const res = await dispatch(
          fetchApplicationDetailsRow(applicationId as any),
        ).unwrap();
        setApplicantData(res);
      } else if (access) {
        const res = await dispatch(
          fetchApplicationDetails(applicationId as any),
        ).unwrap();
        setApplicantData(res);
      }
    } catch (error) {
      console.error("Error fetching application details:", error);
      toast.error("Failed to load application details");
    }
  };

  // Fetch fees data
  // const fetchFeesData = async () => {
  //   if (!applicationId) return;

  //   try {
  //     const [feesComponent, feesAmount] = await Promise.all([
  //       dispatch(FeesDetailsComponentThunk({ applicationId })).unwrap(),
  //       dispatch(FeesDetailsThunk({ applicationId })).unwrap(),
  //     ]);
  //     setProvisionalFee(feesAmount || {});
  //   } catch (error) {
  //     console.error("Error fetching fees details:", error);
  //   }
  // };

  useEffect(() => {
    // fetchFeesData();
    fetchApplicationData();
  }, [applicationId]);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    if (!file) {
      toast.error("Please select a file.");
      return;
    }

    try {
      // 1️⃣ Get Signed URL
      if (file?.size > 21 * 1024 * 1024) {
        toast.error("File size must be less than 20 MB");
        return;
      }
      const fileURL = await dispatch(
        genericUploadThunk({
          file,
          moduleType: row ? "row" : "access",
        }),
      );

      if (!fileURL?.payload?.success) {
        const details =
          fileURL?.payload?.details ||
          fileURL?.payload?.response?.data?.details;

        toast.error(
          Array.isArray(details)
            ? details[0]
            : typeof details === "string"
              ? details
              : "Something Went Wrong!",
        );

        setBankGuaranteeDoc(null);
        return;
      }

      const signedUrl = fileURL?.payload?.data?.signedUrl;
      if (!productionCheck) {
        // 2️⃣ Upload to S3
        const uploadResult = await dispatch(
          uploadToS3Thunk({
            signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap(); // 🔥 important

        if (!uploadResult) {
          throw new Error("S3 upload failed");
        }
      }

      // 3️⃣ Only set state AFTER successful upload
      setBankGuaranteeDoc(file);
      setBankGauranteeURL(fileURL.payload.data.objectKey);

      toast.success("File uploaded successfully.");
    } catch (error) {
      console.error("Error uploading file:", error);

      // ✅ Remove file if anything fails
      setBankGuaranteeDoc(null);
      setBankGauranteeURL(null);

      toast.error("File upload failed.");
    }
  };

  const handleViewFile = (
    document_name?: string,
    isRow?: boolean,
    dispatch?: any,
  ) => {
    if (!dispatch) return;

    return openFileBySignedUrl({
      document_name,
      dispatch,
      getSignedUrlThunk: isRow ? getRowSignedURLThunk : getAccessSignedURLThunk,
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!applicationId) {
      toast.error("Application ID is missing.");
      return;
    }
    try {
      const payload = {
        application_id: applicationId,
        bankGuarantee_no: bankGuaranteeNumber,
        bg_bank_name: bankName,
        bg_issue_date: bgIssuanceDate,
        bg_validity_date: bgValidityDate,
        bg_claim_date: bgClaimDate,
        branch_address: branchAddress,
        bankGuaranteeDocument: {
          fileName: bankGuaranteeDoc?.name || "",
          objectKey: bankGauranteeURL || "",
        },
      };
      const response = await dispatch(
        postFinalBgReUploadThunk(payload),
      ).unwrap();
      if (response?.success) {
        setIsOpen(true);
      } else {
        toast.error("Submission failed. Please try again.");
      }
    } catch (error) {
      console.error(
        "Error submitting electronic Bank Guarantee (e-BG):",
        error,
      );
      toast.error("Submission failed. Please try again.");
    }
  };

  const issuanceDayjs = bgIssuanceDate ? dayjs(bgIssuanceDate) : null;
  const validityDayjs = bgValidityDate ? dayjs(bgValidityDate) : null;
  const bgClaimDateDayJS = bgClaimDate ? dayjs(bgClaimDate) : null;

  return (
    <Box>
      <div className="page-content">
        {/* <DashboardHeader /> */}
        <Box className="app-details-card">
          <Box sx={{ px: { xs: 0, sm: 4 } }}>
            {/* Header Section */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                mb: 2,
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={() => navigate("/application-dashboard")}
                  sx={{
                    mr: { xs: 0, sm: 1 },
                    color: "#222",
                    p: { xs: 0.5, sm: 1 },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ flex: 1, fontSize: { xs: "16px", sm: "24px" } }}
                >
                  electronic Bank Guarantee (e-BG) Submit
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right", mt: { xs: 1, sm: 0 } }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                >
                  Application No:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                >
                  {applicantData?.application_code}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Main Content */}
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                mb={2}
                sx={{ fontSize: { xs: "16px", sm: "24px" } }}
              >
                electronic Bank Guarantee (e-BG)
              </Typography>

              <Typography
                className="body-m"
                color="text.secondary"
                mb={2}
                display="inline-flex"
                sx={{
                  fontSize: { xs: "12px", sm: "14px" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 0.5, sm: 0.5 },
                }}
              >
                <InfoOutlined sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
                &nbsp; An electronic Performance Bank Guarantee (e-PBG) of
                &nbsp; <b> ₹{"2,50,000"} </b>&nbsp; is hereby stipulated as a
                mandatory condition for the processing of the application.
              </Typography>

              {/* Bank Details Form */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 2 },
                  mb: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontWeight={500}
                    mb={1}
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    Bank Name <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    value={bankName}
                    onChange={(e) =>
                      setBankName(validateBankName(e.target.value))
                    }
                    placeholder="Enter Bank Name"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontWeight={500}
                    mb={1}
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    electronic Bank Guarantee (e-BG) Number{" "}
                    <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    value={bankGuaranteeNumber}
                    onChange={(e) =>
                      setBankGuaranteeNumber(
                        validateBankGuaranteeNumber(e.target.value),
                      )
                    }
                    placeholder="Enter Number"
                  />
                </Box>
              </Box>

              {/* Row 2: Issuance Date + Validity Date */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 2 },
                  mb: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontWeight={500}
                    mb={1}
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    electronic Bank Guarantee (e-BG) Issuance Date{" "}
                    <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      open={openIssuanceDate}
                      onOpen={() => setOpenIssuanceDate(true)}
                      onClose={() => setOpenIssuanceDate(false)}
                      value={issuanceDayjs}
                      format="DD/MM/YYYY"
                      onChange={(newValue) => {
                        if (newValue) {
                          setBgIssuanceDate(newValue.toISOString());
                          setBgValidityDate(""); // reset validity date
                        } else {
                          setBgIssuanceDate("");
                        }
                      }}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          placeholder:
                            "electronic Bank Guarantee (e-BG) Issuance Date",

                          inputProps: {
                            readOnly: true,
                          },
                          onClick: () => {
                            // if (issuanceDayjs) {
                            setOpenIssuanceDate(true);
                            // }
                          },
                        },
                        popper: { sx: { zIndex: 20000 } },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontWeight={500}
                    mb={1}
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    electronic Bank Guarantee (e-BG) Validity Date{" "}
                    <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      open={openValidityDate}
                      onOpen={() => setOpenValidityDate(true)}
                      onClose={() => setOpenValidityDate(false)}
                      value={validityDayjs}
                      format="DD/MM/YYYY"
                      disabled={!issuanceDayjs}
                      minDate={issuanceDayjs || undefined}
                      maxDate={
                        issuanceDayjs
                          ? issuanceDayjs.add(row ? 2 : 4, "year")
                          : undefined
                      }
                      onChange={(newValue) => {
                        if (newValue) {
                          setBgValidityDate(newValue.toISOString());
                        } else {
                          setBgValidityDate("");
                        }
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          placeholder: issuanceDayjs
                            ? "electronic Bank Guarantee (e-BG) Validity Date"
                            : "Select Issuance Date first",
                          error: !issuanceDayjs,
                          helperText:
                            !issuanceDayjs && "Issuance Date is required",
                          inputProps: {
                            readOnly: true,
                          },
                          onClick: () => {
                            if (issuanceDayjs) {
                              setOpenValidityDate(true);
                            }
                          },
                        },

                        popper: { sx: { zIndex: 20000 } },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>

              {/* Row 3: Claim Date + Branch Address */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 2 },
                  mb: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontWeight={500}
                    mb={1}
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    electronic Bank Guarantee (e-BG) Claim Date{" "}
                    <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      open={openClaimDate}
                      onOpen={() => setOpenClaimDate(true)}
                      onClose={() => setOpenClaimDate(false)}
                      value={bgClaimDateDayJS}
                      format="DD/MM/YYYY"
                      disabled={!issuanceDayjs}
                      minDate={issuanceDayjs || undefined}
                      maxDate={
                        issuanceDayjs
                          ? row
                            ? issuanceDayjs.add(24, "month")
                            : issuanceDayjs.add(4, "year")
                          : undefined
                      }
                      onChange={(newValue) => {
                        if (newValue) {
                          setBgClaimDate(newValue.toISOString());
                        } else {
                          setBgClaimDate("");
                        }
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          inputProps: {
                            readOnly: true,
                          },
                          onClick: () => {
                            if (issuanceDayjs) {
                              setOpenClaimDate(true);
                            }
                          },
                          placeholder: issuanceDayjs
                            ? "electronic Bank Guarantee (e-BG) Claim Date"
                            : "Select Issuance Date first",
                          error: !issuanceDayjs,
                          helperText:
                            !issuanceDayjs && "Issuance Date is required",
                        },
                        popper: { sx: { zIndex: 20000 } },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    fontWeight={500}
                    mb={1}
                    sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                  >
                    Branch Address <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>

                  <TextField
                    required
                    fullWidth
                    size="small"
                    placeholder="Enter Branch Address"
                    value={branchAddress}
                    onChange={(e) =>
                      setBranchAddress(validateBranchAddress(e.target.value))
                    }
                  />
                </Box>
              </Box>

              {/* Document Upload */}
              <Box mb={2}>
                <Typography
                  fontWeight={500}
                  mb={1}
                  sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                >
                  electronic Bank Guarantee (e-BG) Document{" "}
                  <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    border: "1px solid #4B2670",
                    color: "#4B2670",
                    bgcolor: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: { xs: 2, sm: 2 },
                    py: { xs: 0, sm: 0.5 },
                    borderRadius: 2,
                    boxShadow: "none",
                    fontSize: 12,
                  }}
                >
                  <FileUploadOutlinedIcon /> Upload
                  <input
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                </Button>

                {bankGuaranteeDoc && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                      backgroundColor: "#f5f5f5",
                      px: { xs: 1, sm: 1.5 },
                      py: 0.8,
                      borderRadius: 2,
                      width: "fit-content",
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    {/* File Name */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "11px", sm: "14px" },
                        wordBreak: "break-word",
                      }}
                    >
                      {bankGuaranteeDoc?.name}
                    </Typography>

                    {/* Download / Preview Icon */}
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleViewFile(
                          bankGauranteeURL?.split("/")?.pop(),
                          !!row,
                          dispatch,
                        )
                      }
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>

                    {/* Remove Icon */}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setBankGuaranteeDoc(null);
                        setBankGauranteeURL(null);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {/* Note Section */}
              <Typography
                className="body-m"
                color="text.secondary"
                mb={2}
                sx={{ fontSize: { xs: "11px", sm: "12px" } }}
              >
                <Typography
                  className="body-m"
                  color="#B3261D"
                  display="inline"
                  sx={{ fontSize: { xs: "11px", sm: "12px" } }}
                >
                  <strong>Note:</strong>{" "}
                </Typography>
                The Performance electronic Bank Guarantee (e-BG) shall remain
                valid for a period of{" "}
                {row ? "one (1) year (renew if required)" : "three (3) years"}{" "}
                and shall be released by the Highway Administrator within thirty
                (30) days of the grant of regular access permission. In the
                event the access construction is not completed within the
                stipulated or extended period, the Highway Administration
                reserves the right to encash the electronic Bank Guarantee
                (e-BG).
              </Typography>

              <Divider sx={{ mt: 2 }} />

              {/* Submit Button */}
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#3a1c73",
                    color: "#fff",
                    borderRadius: 100,
                    fontWeight: 600,
                    fontSize: { xs: "12px", sm: "1rem" },
                    mt: 1,
                    py: { xs: 1, sm: 1.2 },
                    px: { xs: 2, sm: 4 },
                    boxShadow: "none",
                    height: { xs: 32, sm: 35 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                  disabled={
                    !bankGuaranteeDoc ||
                    !bgIssuanceDate ||
                    !bgValidityDate ||
                    !bgClaimDate ||
                    !branchAddress ||
                    !bankName ||
                    !bankGuaranteeNumber
                  }
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
        <BgConfirmationModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          moduleType={access}
        />
      </div>
    </Box>
  );
};
